import { logSwallowed } from '@/lib/errors';
import { confirmDialog, alertDialog } from '@/lib/dialogs';
import { eventBusHub } from '@/lib/eventBusHub';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trash2, Check, X, Minus, Plus, RefreshCw, Folder, Settings, CalendarDays, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/widgets/DataTableWidget';
import { ObjectCard, type CardField, type CardTemplateDef } from '@/components/ui/ObjectCard';
import { listTagsForSubjects, type TagInfo } from '@/lib/tags';
import { logErrorToJournal } from '@/lib/errors';
import { onDataChanged } from '@/lib/dataEvents';
import { useContextChatStore } from '@/lib/contextChat';
import { CardGrid, cardNarrowClass } from '@/components/ui/CardGrid';
import { ListToolbar } from '@/components/ui/ListToolbar';
import { GroupedList } from '@/components/autoforms/GroupedList';
import { ListSettingsDialog } from '@/components/autoforms/ListSettingsDialog';
import { PeriodFilterDialog } from '@/components/autoforms/PeriodFilterDialog';
import { PrintListDialog } from '@/components/autoforms/PrintListDialog';
import { settingsStore } from '@/lib/settingsStore';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { isElementVisible, loadVisibilityOptions } from '@/lib/visibility';
import { viewForDevice, parseViewSettings } from '@/lib/viewSettings';
import { formatValue, loadConfigFormats, subscribeConfigFormats } from '@/lib/format';
import { subscribeVisibility } from '@/lib/visibility';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { styleCss } from '@/components/ui/StyleBadge';
import { applyFormats, type CondFormat, type CondContext } from '@/lib/conditionalFormat';
import { dataService } from '@/lib/dataService';
import type { ListFilter } from '@/types/formProperties';
import { createLogger } from '@/lib/logger';
import { CopyButton } from '@/components/ui/CopyButton';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { readThrough, useOfflineDataStore } from '@/lib/offlineCache';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { filterPeople } from '@/lib/people';
import { cacheGetSavedAt } from '@/lib/storage/persistedCache';
import { useI18n } from '@/lib/i18n';

const log = createLogger('AutoListForm');

// Размер страницы курсорной выдачи (keyset, без OFFSET) списков задач/заявок:
// большие базы грузятся страницами, «Показать ещё» — следующая страница.
const LIST_CURSOR_PAGE = 200;

import {
  applySearch, applyFilters, RefCell, buildColumnsFromProto, decorateWithChatBadge,
  type AutoListFormProps,
} from './autoListHelpers';
// Реэкспорт для внешних потребителей (MiniDynamicList/TasksPage) — контракт сохранён.
export { applySearch, applyFilters };
export function AutoListForm({
  objectType, objectName, onRowDoubleClick, commands, filters, serverFilters, onCreate, onDelete, onSelect, showToolbar = true, visibleKeys, table: tableOverride, selectionMode = '', viewMode, objectViewSettings, rights: rightsProp,   singleClickOpen, taskFolder, foldersPanel, voiceSearch = true, groupMode, groupBy, forceEmpty = false,
}: AutoListFormProps) {
  const { t, locale } = useI18n();
  // ГРУППИРОВКИ (единый механизм, любые списки): поля — CSV; поле может быть с
  // ЧАСТЬЮ ДАТЫ — «created_at:year|month|day» (пример: Статус→год→месяц→день).
  // Подпись группы — презентация ссылки ({поле}_presentation), для статусов —
  // status_name (русский).
  const monthLabel = (mi: number): string => new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2020, mi, 1));
  const datePart = (v: unknown): { y: string; m: string; d: string } => {
    const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? { y: m[1], m: m[2], d: m[3] } : { y: '', m: '', d: '' };
  };
  const groupValueOf = (fieldSpec: string, row: Record<string, unknown>): string => {
    const [col, part] = fieldSpec.split(':');
    if (part === 'year' || part === 'month' || part === 'day') {
      const p = datePart(row[col]);
      if (!p.y) return '';
      if (part === 'year') return p.y;
      if (part === 'month') return `${p.y}-${p.m}`;
      return `${p.y}-${p.m}-${p.d}`;
    }
    const v = row[col];
    return v === null || v === undefined ? '' : String(v);
  };
  const groupLabel = (fieldSpec: string, value: unknown, sample?: Record<string, unknown>): string => {
    const [col, part] = fieldSpec.split(':');
    const raw = value === null || value === undefined ? '' : String(value);
    if (part === 'year' || part === 'month' || part === 'day') {
      if (!raw) return t('grouping.empty');
      if (part === 'year') return raw;
      if (part === 'month') {
        const [y, m] = raw.split('-');
        const mi = Math.max(0, Math.min(11, Number(m) - 1));
        return `${monthLabel(mi)} ${y}`;
      }
      const [y, m, d] = raw.split('-');
      return `${d}.${m}.${y}`;
    }
    if (raw === '') return t('grouping.empty');
    const pres = String((sample as Record<string, unknown> | undefined)?.[col + '_presentation'] || '');
    if (pres) return pres;
    if (col === 'status_code') {
      const sn = String((sample as Record<string, unknown> | undefined)?.status_name || '');
      if (sn) return sn;
    }
    // Перечисление: подпись группы — СИНОНИМ значения (SSoT — сервер, getEnum);
    // карта уже загружена для колонок этого списка (код — только если недоступно).
    const enumMap = enumLabels[col];
    if (enumMap && enumMap[raw]) return enumMap[raw];
    return raw;
  };
  // НАСТРОЙКИ СПИСКА (SRP/SSoT, per-user): пользовательское перекрытие группировок
  // из хранилища настроек («Ещё → Настройки»); перекрывает настройки формы/виджета
  // (конфигуратора) и хранится по списку+пользователю (user_settings).
  const groupSettingsKey = `list.${objectType}.${objectName}.group`;
  const readGroupOverride = (): { mode: string; by: string } | null => {
    const v = settingsStore.getJSON<{ mode?: string; by?: string } | null>(groupSettingsKey, null);
    return v && (v.mode || v.by) ? { mode: String(v.mode || ''), by: String(v.by || '') } : null;
  };
  const [groupOverride, setGroupOverride] = useState<{ mode: string; by: string } | null>(readGroupOverride);
  const [listSettingsOpen, setListSettingsOpen] = useState(false);
  const [printListOpen, setPrintListOpen] = useState(false);
  useEffect(() => {
    const unsub = settingsStore.subscribe(() => setGroupOverride(readGroupOverride()));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSettingsKey]);
  const effGroupMode = groupOverride ? groupOverride.mode : String(groupMode || '');
  const effGroupBy = groupOverride ? groupOverride.by : String(groupBy || '');
  const groupFields = String(effGroupBy || '').split(',').map((s) => s.trim()).filter(Boolean);
  // ПОЛЬЗОВАТЕЛЬСКИЕ ОТБОРЫ (SRP/SSoT): «Ещё → Настройки» → вкладка «Отборы»;
  // хранятся по списку и пользователю (settingsStore) и применяются ВМЕСТЕ с
  // отборами формы/виджета (AND). Пример: период — created_at >= «с» и <= «по».
  const filtersSettingsKey = `list.${objectType}.${objectName}.filters`;
  const readUserFilters = (): ListFilter[] => {
    const v = settingsStore.getJSON<ListFilter[] | null>(filtersSettingsKey, null);
    return Array.isArray(v) ? (v as ListFilter[]) : [];
  };
  const [userFilters, setUserFilters] = useState<ListFilter[]>(readUserFilters);
  useEffect(() => {
    const unsub = settingsStore.subscribe(() => setUserFilters(readUserFilters()));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSettingsKey]);
  const effFilters = useMemo(
    () => [...(filters || []), ...userFilters],
    [filters, userFilters],
  );
  // ПЕРИОД (по дате создания, «Ещё → Период») — те же пользовательские отборы:
  // created_at >= «с» и <= «по» (конец дня T23:59:59 — включает выбранный день).
  const periodFrom = String(userFilters.find((f) => f.field === 'created_at' && f.condition === '>=')?.value || '');
  const periodToRaw = String(userFilters.find((f) => f.field === 'created_at' && f.condition === '<=')?.value || '');
  const periodTo = periodToRaw.includes('T') ? periodToRaw.split('T')[0] : periodToRaw;
  const hasPeriod = !!(periodFrom || periodToRaw);
  const [periodOpen, setPeriodOpen] = useState(false);
  const applyPeriod = (from: string, to: string) => {
    const rest = userFilters.filter((f) => !(f.field === 'created_at' && (f.condition === '>=' || f.condition === '<=')));
    const next: ListFilter[] = [...rest];
    if (from) next.push({ field: 'created_at', condition: '>=', value: from });
    if (to) next.push({ field: 'created_at', condition: '<=', value: `${to}T23:59:59` });
    settingsStore.setJSON(filtersSettingsKey, next);
  };
  const resetPeriod = () => {
    const rest = userFilters.filter((f) => !(f.field === 'created_at' && (f.condition === '>=' || f.condition === '<=')));
    settingsStore.setJSON(filtersSettingsKey, rest);
  };
  // ВНИМАНИЕ: type объявлен ДО хуков — используется в зависимостях useEffect
  // (иначе TDZ: «Cannot access 'type' before initialization»)
  const type = objectType.toLowerCase();
  const isDocument = type === 'document' || type === 'documents';
  // Непрочитанные в обсуждении (контекстный чат): бейдж на карточках
  // (SSoT — contextChat; счётчики обновляются по событиям чата)
  const chatUnreadFor = useContextChatStore((s) => s.unreadFor);
  const { isMobile } = useBreakpoint();
  // «Виды» объекта метаданных (десктоп/мобильный) — ЕДИНЫЙ механизм (как в 1С):
  // SSoT — view_settings объекта (сервер); AutoListForm САМ читает их везде
  // (Предприятие, конфигуратор, встроенные списки форм). Явный viewMode
  // (вид элемента формы dynamic_list) имеет приоритет — как в 1С.
  const [selfView, setSelfView] = useState<{ view?: string; cardTemplate?: string } | undefined>(undefined);
  const [cardTemplateDef, setCardTemplateDef] = useState<CardTemplateDef | undefined>(undefined);
  useEffect(() => {
    if (!objectType || !objectName) return;
    if (type !== 'catalog' && type !== 'document' && type !== 'task') return;
    let cancelled = false;
    (async () => {
      try {
        const sdk = getSDK();
        const meta: any = type === 'document'
          ? await sdk.metadata.getDocument({ id: objectName })
          : type === 'task'
            ? await sdk.task.getTaskType({ code: objectName })
            : await sdk.metadata.getCatalog({ id: objectName });
        if (cancelled) return;
        const vs = parseViewSettings(String(meta?.viewSettings || ''));
        setSelfView(viewForDevice(vs, 'list', isMobile));
      } catch (e) { logSwallowed(e, 'components/autoforms/AutoListForm.tsx:184'); }
    })();
    return () => { cancelled = true; };
  }, [type, objectType, objectName, isMobile]);

  // Шаблон карточки из «Видов» объекта (SSoT — сервер, metadata_card_templates)
  const viewCardTemplate = objectViewSettings?.cardTemplate || selfView?.cardTemplate || '';
  useEffect(() => {
    if (!viewCardTemplate) { setCardTemplateDef(undefined); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await getSDK().metadata.getCardTemplate({ id: viewCardTemplate });
        if (cancelled) return;
        let def: CardTemplateDef = {};
        try { def = typeof (r as any)?.definition === 'string' ? JSON.parse((r as any).definition) : ((r as any)?.definition || {}); } catch { def = {}; }
        setCardTemplateDef(def);
      } catch (e) { logSwallowed(e, 'components/autoforms/AutoListForm.tsx:201'); }
    })();
    return () => { cancelled = true; };
  }, [viewCardTemplate]);

  // Вид по умолчанию — КАРТОЧКИ во всех режимах (как в 1С 8.5):
  // явный viewMode элемента формы > view_settings объекта > 'cards'.
  const effectiveViewMode = (viewMode || objectViewSettings?.view || selfView?.view || 'cards') as 'list' | 'tree' | 'flat' | 'cards';
  // Ключ баннера «данные из кэша» — ТОТ ЖЕ, что у чтения (offlineCache.readThrough)
  const offlineBannerKey = (type === 'task' || type === 'tasks')
    ? `list:task:${objectName}:${taskFolder || ''}`
    : (type === 'order' || type === 'orders')
      ? `list:order:${objectName}`
      : `list:${type}:${objectName}`;
  const offlineBannerVisible = useOfflineDataStore((s) => !!s.stale[offlineBannerKey]);
  // ДИАГНОСТИКА: какой вид реально применяется.
  useEffect(() => {
    createLogger('AutoListForm')?.debug?.('view', effectiveViewMode);
  }, [effectiveViewMode]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<Array<{ key: string; label: string; sortable: boolean }>>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // МУЛЬТИ-ВЫБОР в карточках (long-press 500мс — работает и на телефонах):
  // режим выбора → клик выбирает/снимает; тулбар: «Пометить на удаление» /
  // «Удалить окончательно» (админ) / «Готово».
  const [multiSelect, setMultiSelect] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const startLongPress = (id: string) => {
    if (longPressTimer.current) return;
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      setMultiSelect(true);
      // Удержание — ПЕРЕКЛЮЧЕНИЕ выделения (установить/снять); клик всегда открывает.
      setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) { window.clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };
  const toggleSelect = (id: string) => {
    if (longPressFired.current) { longPressFired.current = false; return; }
    setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  // Esc — выход из режима мультивыбора (если передумал; плюс кнопка «Готово»)
  useEffect(() => {
    if (!multiSelect) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMultiSelect(false); setSelectedIds(new Set()); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiSelect]);
  const [loading, setLoading] = useState(true);
  // Была ли хоть одна загрузка: фон-перезагрузки делаем БЕЗ мигания (silent).
  const hasLoadedRef = useRef(false);
  // КУРСОРНАЯ ПАГИНАЦИЯ (keyset, без OFFSET) списков задач/заявок: сервер
  // отдаёт страницу + nextCursor ('' = конец). Загрузка «Показать ещё» —
  // увеличиваем число страниц (pagesRef) и перечитываем; фоновые обновления
  // списка СОХРАНЯЮТ глубину загрузки.
  const [nextCursor, setNextCursor] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const pagesRef = useRef(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Условное оформление строк списка (как в 1С, на стилях)
  const [condRules, setCondRules] = useState<CondFormat[]>([]);
  const [condStyles, setCondStyles] = useState<Record<string, any>>({});
  const condCtx: CondContext = useMemo(() => ({
    currentUserId: useAuthStore.getState().user?.id || '',
    currentUserRoles: useAuthStore.getState().user?.roles || [],
    isAdmin: useAuthStore.getState().isAdmin(),
  }), []);
  // Иерархия справочника (как в 1С): '' | 'groups_and_items' | 'items'
  const [hierarchyType, setHierarchyType] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Права текущего пользователя (как в 1С): из GetCatalog/GetDocument.rights
  const [rights, setRights] = useState<AutoListFormProps['rights']>(rightsProp || null);
  // «Показывать помеченные» (как в 1С): только при праве can_view_marked
  const [showMarked, setShowMarked] = useState(false);
  // Поиск — ОБЩИЙ для всех видов (list/tree/flat/cards): тот же список,
  // меняется только отображение элементов (как в 1С)
  const [searchText, setSearchText] = useState('');
  // Синонимы значений перечислений: {колонка: {код: синоним}} — списки/карточки
  // показывают синоним, а не код (SSoT значений — сервер, getEnum).
  const [enumLabels, setEnumLabels] = useState<Record<string, Record<string, string>>>({});
  // Одна колонка карточек — по РАЗМЕРУ КОНТЕЙНЕРА (не по окну/устройству):
  // ResizeObserver замеряет поле размещения; при ширине < 560px — вплотную
  // с разделителями (зазоры убираем), иначе — сетка с отступами.


  // Включённые команды (пусто/undefined = все)
  const hasCmd = useCallback((cmd: string) => !commands || commands.length === 0 || commands.includes(cmd), [commands]);
  // Право на команду: явные права (пропс/сервер) — блокируют; иначе команда доступна
  const canCmd = useCallback((cmd: string, flag?: boolean) => {
    if (!hasCmd(cmd)) return false;
    if (rights && typeof flag === 'boolean') return flag;
    return true;
  }, [hasCmd, rights]);

  const schema = 'public';
  const table = tableOverride || (type === 'entity' || type === 'entities' ? 'entities' : objectName.toLowerCase());

  // Иерархическая сборка строк дерева (для иерархических справочников,
  // если вид 'tree' или не задан; вид 'flat'/'cards' — плоский список)
  const showTree = effectiveViewMode === 'tree' || (hierarchyType && effectiveViewMode !== 'flat' && effectiveViewMode !== 'cards');
  const buildTreeRows = (all: Record<string, unknown>[]) => {
    if (!showTree) return all;
    const childrenMap: Record<string, Record<string, unknown>[]> = {};
    const roots: Record<string, unknown>[] = [];
    for (const r of all) {
      const pid = String(r.parent_id ?? '');
      if (pid && pid.length === 36) {
        (childrenMap[pid] = childrenMap[pid] || []).push(r);
      } else {
        roots.push(r);
      }
    }
    const flat: Record<string, unknown>[] = [];
    const walk = (list: Record<string, unknown>[], depth: number) => {
      for (const r of list) {
        const hasChildren = (childrenMap[String(r.id)] || []).length > 0;
        const expanded = expandedIds.has(String(r.id));
        flat.push({ ...r, __depth: depth, __hasChildren: hasChildren, __expanded: expanded, __isGroup: r.is_group === true || r.is_group === '1' || r.is_group === 'true' });
        if (hasChildren && expanded) walk(childrenMap[String(r.id)], depth + 1);
      }
    };
    walk(roots, 0);
    return flat;
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Загрузка данных через DataService
  const loadData = async (opts?: { silent?: boolean }) => {
    // Отбор-привязка не заполнен (резолв lib/dlFilters: @objectId новой записи,
    // незаданный @param/@context) — список ЗАВЕДОМО пуст: НЕ грузим данные
    // «без отбора» (иначе показывались бы ВСЕ записи базы). Пустое состояние
    // рисует обычная ветка «Нет данных».
    if (forceEmpty) {
      setRows([]);
      setError(null);
      hasLoadedRef.current = true;
      setLoading(false);
      return;
    }
    // Фон-перезагрузки (события данных, повторные вызовы) — БЕЗ мигания:
    // loading показывается только до ПЕРВОЙ загрузки; далее список остаётся
    // на экране, пока не придут новые строки (иначе виджеты «помигивали»).
    const silent = opts?.silent ?? hasLoadedRef.current;
    if (!silent) setLoading(true);
    setError(null);
    setEnumLabels({});
    // Курсор задач/заявок по умолчанию пуст — ветки task/order перезапишут его
    // серверным nextCursor ('' = конец списка, кнопка «Показать ещё» скрыта).
    setNextCursor('');
    try {
      if ((type === 'catalog' || type === 'document' || type === 'register') && objectName) {
        // Метаданные (для колонок)
        const sdk = getSDK();
        let definition: any = null;
        // Регистр: физическая таблица данных — movements_table (накопления)
        // или код (сведения); колонки — ПОЛЯ регистра (единый механизм)
        let dataTable = table;
        if (type === 'catalog') {
          definition = (await readThrough(`meta:catalog:${objectName}`, () => sdk.metadata.getCatalog({ id: objectName }))).data;
        } else if (type === 'document') {
          definition = (await readThrough(`meta:document:${objectName}`, () => sdk.metadata.getDocument({ id: objectName }))).data;
        } else {
          definition = (await readThrough(`meta:register:${objectName}`, () => sdk.metadata.getRegister({ id: objectName }))).data;
          dataTable = (definition as any)?.movementsTable || table;
        }
        setHierarchyType(String((definition as any)?.hierarchyType || ''));
        // Права текущего пользователя (как в 1С): сервер заполняет rights по ролям JWT
        if (rightsProp) {
          setRights(rightsProp);
        } else if ((definition as any)?.rights) {
          setRights((definition as any).rights);
        }
        const fields = type === 'register'
          ? (await readThrough(`fields:register:${objectName}`, async () =>
              ((await sdk.metadata.listFields({ entityType: 'register', entityCode: objectName })) as any)?.items || [],
            )).data
          : (definition as any)?.fields || [];
        const systemFields = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by',
                              'is_marked', 'is_posted', 'posted_at'];
        const cols = fields
          .filter((f: any) => !systemFields.includes(f.code || f.name))
          .map((f: any) => {
            const dt = (f.dataType || f.data_type || '').toLowerCase();
            const refTable = f.refTable || f.refCatalogCode || f.ref_table || '';
            const isRef = (dt === 'uuid' || dt === 'reference') && !!refTable;
            return {
              name: f.code || f.name,
              label: f.synonym || f.name || f.code,
              dataType: f.dataType || f.data_type,
              refTable: isRef ? refTable : '',
              format: (f as any).format || '',
              // Скрыто по умолчанию (как в 1С): поле не выводится в АВТО-колонках,
              // но доступно при ЯВНОМ выборе (колонки макета кастомной формы)
              hiddenByDefault: !!(f as any).hiddenByDefault || !!(f as any).hidden_by_default,
            };
          });
        // Системные колонки (как в 1С): владелец/родитель/группа/пометка/аудит —
        // не реквизиты метаданных, но могут выводиться в списке (в т.ч. из макета
        // кастомной формы списка). owner_id/parent_id — ссылки: представление
        // из серверной {col}_presentation (как в 1С)
        const systemCols = [
          { name: 'owner_id', label: t('list.column.owner'), dataType: 'uuid', refTable: '', isRef: true },
          { name: 'parent_id', label: t('list.column.parent'), dataType: 'uuid', refTable: '', isRef: true },
          { name: 'is_group', label: t('list.column.group'), dataType: 'boolean', refTable: '' },
          { name: 'is_marked', label: t('list.column.marked'), dataType: 'boolean', refTable: '' },
          { name: 'created_by', label: t('list.column.created_by'), dataType: 'text', refTable: '' },
          { name: 'updated_by', label: t('list.column.updated_by'), dataType: 'text', refTable: '' },
          { name: 'created_at', label: t('list.column.created_at'), dataType: 'timestamp', refTable: '' },
          { name: 'updated_at', label: t('list.column.updated_at'), dataType: 'timestamp', refTable: '' },
        ];
        for (const s of systemCols) {
          if (!cols.some((c: any) => c.name === s.name)) cols.push(s);
        }
        // Кастомная форма списка: поля макета = колонки таблицы
        // (ЯВНЫЙ выбор пользователя — скрытые по умолчанию поля показываются);
        // авто-колонки: скрытые по умолчанию поля НЕ выводятся (как в 1С)
        const visible = visibleKeys && visibleKeys.length > 0;
        const orderedCols = visible
          ? visibleKeys
              .map((k) => cols.find((c: any) => c.name === k))
              .filter(Boolean)
          : cols;
        // Синонимы значений перечислений (enumeration → селект/бейдж): тянем
        // ТОЛЬКО коды перечислений колонок этого списка (SSoT — сервер)
        const enumLabelsMap: Record<string, Record<string, string>> = {};
        const enumFields = fields.filter((f: any) =>
          String(f.dataType || f.data_type || '').toLowerCase() === 'enumeration' && (f.refTable || f.refCatalogCode));
        if (enumFields.length > 0) {
          const byRef: Record<string, Record<string, string>> = {};
          const refs: string[] = Array.from(new Set<string>(enumFields.map((f: any) => String(f.refTable || f.refCatalogCode))));
          for (const ref of refs) {
            try {
              const r: any = await sdk.metadata.getEnum({ code: ref });
              const m: Record<string, string> = {};
              for (const v of r?.values || []) m[String(v.value)] = String(v.synonym || v.name || v.value);
              byRef[ref] = m;
            } catch { /* перечисление недоступно — значения показываются кодами */ }
          }
          for (const f of enumFields) {
            enumLabelsMap[String(f.code || f.name)] = byRef[String(f.refTable || f.refCatalogCode)] || {};
          }
        }
        setEnumLabels(enumLabelsMap);
        setColumns(buildColumnsFromProto(visible ? orderedCols : cols.filter((c: any) => !c.hiddenByDefault), enumLabelsMap));

        // Данные: помеченные на удаление скрыты по умолчанию (как в 1С);
        // «Показывать помеченные» (право can_view_marked) — прямой серверный
        // запрос с include_marked (в обход кэша: кэш хранит только «чистые» строки)
        if (serverFilters && serverFilters.length > 0) {
          // СЕРВЕРНЫЕ отборы (SSoT — сервер фильтрует ДО выборки): query-отборы
          // сайтов (§2) и точечные равенства. Кэш чтения не используется —
          // набор строк не совпадает с «полным» кэшем.
          const res: any = await sdk.metadata.listTableData({
            schema, table: dataTable, pageSize: 1000, includeMarked: showMarked,
            filters: serverFilters.map((f) => ({ column: f.field, value: String(f.value ?? '') })),
          } as any);
          const items = ((res as any)?.rows || []).map((r: any) => {
            const row: Record<string, unknown> = { id: r.id || '' };
            for (const v of r.values || []) row[v.column] = v.value;
            return row;
          });
          setRows(applyFilters(items, filters));
        } else if (showMarked) {
          const res = await sdk.metadata.listTableData({ schema, table: dataTable, pageSize: 1000, includeMarked: true });
          const items = ((res as any)?.rows || []).map((r: any) => {
            const row: Record<string, unknown> = { id: r.id || '' };
            for (const v of r.values || []) row[v.column] = v.value;
            return row;
          });
          setRows(applyFilters(items, filters));
        } else {
          await dataService.fetchAll(schema, dataTable);
          const cached = dataService.list<Record<string, unknown>>(schema, dataTable);
          setRows(applyFilters(cached, filters));
          // Баннер «данные из кэша»: офлайн — сохранённое (время сохранения
          // кэша таблицы); в сети — свежие данные (пометка снимается).
          const bannerKey = `list:${type}:${objectName}`;
          if (isEffectivelyOffline()) {
            const at = await cacheGetSavedAt(schema, dataTable);
            useOfflineDataStore.getState().markStale(bannerKey, at || Date.now());
          } else {
            useOfflineDataStore.getState().markFresh(bannerKey);
          }
        }
      } else if (type === 'task' || type === 'tasks') {
        // ЗАДАЧА (как Заявка): реквизиты — metadata_fields (parent_type='task');
        // данные — TaskService.listTasks (статус/представления; SSoT — сервер)
        const sdkTask = getSDK();
        const fields = (await readThrough(`fields:task:${objectName}`, async () =>
          ((await sdkTask.metadata.listFields({ entityType: 'task', entityCode: objectName })) as any)?.items || [],
        )).data;
        const taskSystemFields = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by',
                                  'is_marked', 'is_posted', 'posted_at', 'status_code', 'assignee_id', 'author_id'];
        const cols = fields
          .filter((f: any) => !taskSystemFields.includes(f.code || f.name))
          .map((f: any) => {
            const dt = (f.dataType || f.data_type || '').toLowerCase();
            const refTable = f.refTable || f.refCatalogCode || f.ref_table || '';
            const isRef = (dt === 'uuid' || dt === 'reference') && !!refTable;
            return {
              name: f.code || f.name,
              label: f.synonym || f.name || f.code,
              dataType: f.dataType || f.data_type,
              refTable: isRef ? refTable : '',
              format: (f as any).format || '',
              hiddenByDefault: !!(f as any).hiddenByDefault || !!(f as any).hidden_by_default,
            };
          });
        // Статус задачи (представление — status_name из TaskService)
        if (!cols.some((c: any) => c.name === 'status_name')) {
          cols.push({ name: 'status_name', label: t('list.column.status'), dataType: 'text', refTable: '' });
        }
        // Загруженность исполнителя: «N активных» (сервер, assignee_active_count)
        if (!cols.some((c: any) => c.name === 'assignee_active_count')) {
          cols.push({ name: 'assignee_active_count', label: t('list.column.active'), dataType: 'number', refTable: '' });
        }
        setColumns(buildColumnsFromProto(cols.filter((c: any) => !c.hiddenByDefault)));
        // КУРСОРНАЯ ВЫДАЧА (keyset): pageSize = страница × глубину загрузки,
        // cursor:'' = первая страница; nextCursor — со СЕРВЕРА ('' = конец).
        const res: any = (await readThrough(`list:task:${objectName}:${taskFolder || ''}`, () => sdkTask.task.listTasks({
          taskType: objectName, folder: taskFolder || '',
          pageSize: LIST_CURSOR_PAGE * pagesRef.current,
          includeMarked: showMarked, cursor: '',
        } as any))).data;
        setNextCursor(String(res?.nextCursor || ''));
        const items = (res?.items || []) as any[];
        const rows: Record<string, unknown>[] = items.map((t: any) => {
          const row: Record<string, unknown> = {
            id: t.id || '',
            name: t.name || '',
            // ОПИСАНИЕ задачи — в карточку/список (в т.ч. строки из заявки).
            description: t.description || '',
            // Дата создания — для группировок по датам (год → месяц → день).
            created_at: t.createdAt || '',
            status_name: t.statusName || t.statusCode || '',
            assignee_active_count: Number(t.assigneeActiveCount || 0),
            // Пометка удаления (показ по чекбоксу «Показывать помеченные»)
            is_marked: !!t.isMarked,
            // Системные поля задачи — для КЛИЕНТСКИХ отборов динамического списка
            // (applyFilters): assignee_id = @current_user («Мои задачи») и т.п.
            assignee_id: t.assigneeId || '',
            author_id: t.authorId || '',
            status_code: t.statusCode || '',
            due_date: t.dueDate || '',
            priority: t.priority || '',
          };
          for (const [k, v] of Object.entries((t.extraFields || {}) as Record<string, string>)) {
            row[k] = v;
          }
          return row;
        });
        setRows(applyFilters(rows, filters));
        // Офлайн-фёрст: строки списка задач попадают и в кэш ЧТЕНИЯ таблицы
        // типа — открытие формы записи без сервера (dataService.fetchById).
        dataService.localCache.merge(schema, objectName, rows as any);
      } else if (type === 'order' || type === 'orders') {
        // ЗАЯВКА (встроенный тип, как Задачи): колонки — реквизиты типа
        // (listFields parent_type='order'); данные — OrderService.listOrders
        // (автономер, статус, представления ссылок; SSoT — сервер)
        const sdkOrder = getSDK();
        const fields = (await readThrough(`fields:order:${objectName}`, async () =>
          ((await sdkOrder.metadata.listFields({ entityType: 'order', entityCode: objectName })) as any)?.items || [],
        )).data;
        const orderSystemFields = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by',
                                   'is_marked', 'is_posted', 'posted_at'];
        const cols = fields
          .filter((f: any) => !orderSystemFields.includes(f.code || f.name))
          .map((f: any) => {
            const dt = (f.dataType || f.data_type || '').toLowerCase();
            const refTable = f.refTable || f.refCatalogCode || f.ref_table || '';
            const isRef = (dt === 'uuid' || dt === 'reference') && !!refTable;
            return {
              name: f.code || f.name,
              label: f.synonym || f.name || f.code,
              dataType: f.dataType || f.data_type,
              refTable: isRef ? refTable : '',
              format: (f as any).format || '',
              hiddenByDefault: !!(f as any).hiddenByDefault || !!(f as any).hidden_by_default,
            };
          });
        // Статус заявки (представление — status_name из OrderService)
        if (!cols.some((c: any) => c.name === 'status_name')) {
          cols.push({ name: 'status_name', label: t('list.column.status'), dataType: 'text', refTable: '' });
        }
        // ФАЙЛЫ заявки: свои + связанных задач (отметка «есть файлы» в карточке/списке)
        if (!cols.some((c: any) => c.name === 'files_count')) {
          cols.push({ name: 'files_count', label: t('list.column.files'), dataType: 'number', refTable: '' });
        }
        setColumns(buildColumnsFromProto(cols.filter((c: any) => !c.hiddenByDefault)));
        // КУРСОРНАЯ ВЫДАЧА (keyset): pageSize = страница × глубину загрузки,
        // cursor:'' = первая страница; nextCursor — со СЕРВЕРА ('' = конец).
        const res: any = (await readThrough(`list:order:${objectName}`, () => sdkOrder.order.listOrders({
          orderType: objectName,
          pageSize: LIST_CURSOR_PAGE * pagesRef.current,
          includeMarked: showMarked, cursor: '',
        } as any))).data;
        setNextCursor(String(res?.nextCursor || ''));
        const items = (res?.items || []) as any[];
        const rows: Record<string, unknown>[] = items.map((o: any) => {
          const row: Record<string, unknown> = {
            id: o.id || '',
            name: o.name || '',
            // НОМЕР заявки — в колонку/карточку (в шапке формы ещё и заголовок).
            number: o.number || '',
            // КОД статуса — для ГРУППИРОВОК (groupBy: status_code); название — status_name.
            status_code: o.statusCode || '',
            status_name: o.statusName || o.statusCode || '',
            // Дата создания — для группировок по датам (год → месяц → день).
            created_at: o.createdAt || '',
            // ФАЙЛЫ (свои + связанных задач) — отметка «есть файлы» в карточке/списке.
            files_count: o.filesCount || 0,
            // Пометка удаления (показ по чекбоксу «Показывать помеченные»)
            is_marked: !!o.isMarked,
          };
          for (const [k, v] of Object.entries((o.extraFields || {}) as Record<string, string>)) {
            row[k] = v;
          }
          return row;
        });
        setRows(applyFilters(rows, filters));
        // Офлайн-фёрст: строки списка заявок попадают и в кэш ЧТЕНИЯ таблицы
        // типа — открытие формы записи без сервера (dataService.fetchById).
        dataService.localCache.merge(schema, objectName, rows as any);
      } else if (type === 'entity' || type === 'entities') {
        const sdkEntities = getSDK();
        // Пикер «Пользователь»: серверный peopleOnly + клиентская защита
        // (tg_*/боты/служебные учётки в выборе не показываются).
        const result = await sdkEntities.entity.listEntities({ peopleOnly: true });
        const items = filterPeople(((result as any).items || []) as any[]);
        setRows(items.map((e: any) => ({
          id: e.id || '', login: e.login || '', name: e.name || '', email: e.email || '',
        })));
        setColumns([
          { key: 'id', label: 'ID', sortable: true },
          { key: 'login', label: t('list.column.login'), sortable: true },
          { key: 'name', label: t('list.column.name'), sortable: true },
          { key: 'email', label: t('list.column.email'), sortable: true },
        ]);
      } else if (type === 'role' || type === 'roles') {
        const sdkRoles = getSDK();
        const result = await sdkRoles.auth.getRoles({});
        const items = (result as any).items || [];
        setRows(items.map((r: any) => ({
          id: r.id || '', name: r.name || '', description: r.description || '',
        })));
        setColumns([
          { key: 'id', label: 'ID', sortable: true },
          { key: 'name', label: t('list.column.name'), sortable: true },
          { key: 'description', label: t('list.column.description'), sortable: true },
        ]);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      log.error('AutoListForm load failed', message);
      setError(message);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  };

  // Актуальная ссылка на loadData для подписок (без устаревших замыканий на filters).
  const loadRef = useRef(loadData);
  loadRef.current = loadData;

  // «Показать ещё» — следующая страница курсорной выдачи (задачи/заявки):
  // глубина загрузки +1 и перечитывание БЕЗ OFFSET (фоново, без спиннера —
  // список остаётся на экране). Больше страниц нет — nextCursor пуст.
  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    pagesRef.current += 1;
    try {
      await loadRef.current({ silent: true });
    } finally {
      setLoadingMore(false);
    }
  };

  // Стабильный ключ отборов: перезагрузка — только при СМЕНЕ отбора, а не на
  // каждое пересоздание массива filters родителем (иначе список мигал).
  const filtersKey = JSON.stringify(filters || []);
  useEffect(() => {
    // Смена списка/отборов/показа помеченных — курсорная выдача с ПЕРВОЙ страницы.
    pagesRef.current = 1;
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectName, filtersKey, forceEmpty, showMarked]);

  // АВТО-ОБНОВЛЕНИЕ по событиям данных (SSoT — сервер): при создании/изменении/
  // удалении записи ЭТОГО объекта (topic data.{type}.{code}.{action}) список
  // перечитывается сам — без кнопки «Обновить». Фильтр по payload (entity_code/
  // table == objectName) — надёжно при любом формате кода.
  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('data.')) return;
      try {
        const p = ev.payload || {};
        const code = p.entity_code || p.table || '';
        if (code && String(code) === objectName) loadRef.current({ silent: true });
      } catch { /* битый payload — пропускаем */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectName]);

  // Настройки конфигурации (SSoT — сервер): часовой пояс + глобальные форматы;
  // загружаем + ре-рендер при загрузке
  const [, setTzTick] = useState(0);
  useEffect(() => {
    loadConfigFormats();
    return subscribeConfigFormats(() => setTzTick((v) => v + 1));
  }, []);
  // Функциональные опции — единая видимость элементов (кнопки/команды)
  const [, setVisTick] = useState(0);
  useEffect(() => {
    loadVisibilityOptions();
    return subscribeVisibility(() => setVisTick((v) => v + 1));
  }, []);

  // Условное оформление строк (scope='list'): правила объекта + стили
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cf, st] = await Promise.all([
          getSDK().metadata.listConditionalFormats({ scope: 'list', target: `${objectType}:${objectName}` } as any),
          getSDK().metadata.listStyles({}),
        ]);
        if (cancelled) return;
        setCondRules((cf as any)?.items || []);
        const sm: Record<string, any> = {};
        for (const s of (st as any)?.items || []) sm[s.name] = s.attributes || {};
        setCondStyles(sm);
      } catch (e) { logSwallowed(e, 'components/autoforms/AutoListForm.tsx:715'); }
    })();
    return () => { cancelled = true; };
  }, [objectType, objectName]);

  // Подписка на изменения кэша — авто-обновление
  useEffect(() => {
    if (type !== 'catalog' && type !== 'document') return;
    if (showMarked) return; // при показе помеченных — данные из прямого серверного запроса
    return dataService.subscribe(schema, table, () => {
      const cached = dataService.list<Record<string, unknown>>(schema, table);
      if (cached.length > 0) setRows(applyFilters(cached, filters));
    });
  }, [type, schema, table, filtersKey, showMarked]);

  // Поиск — ЛОКАЛЬНАЯ фильтрация уже загруженных строк (без перезагрузки:
  // фокус ввода не теряется, мгновенно)
  const visibleRows = useMemo(() => applySearch(applyFilters(rows, effFilters), searchText), [rows, effFilters, searchText]);

  // Теги карточек (чипы): батч-резолв по видимым строкам (SSoT — сервер).
  const [cardTags, setCardTags] = useState<Record<string, TagInfo[]>>({});
  const [tagsTick, setTagsTick] = useState(0);
  useEffect(() => onDataChanged((d) => {
    if (d.entityCode && d.entityCode === objectName) setTagsTick((v) => v + 1);
  }), [objectName]);
  useEffect(() => {
    if (effectiveViewMode !== 'cards' || !type || visibleRows.length === 0) {
      setCardTags((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    let alive = true;
    listTagsForSubjects(
      visibleRows.slice(0, 200).map((r) => ({
        subjectType: type,
        subjectCode: objectName,
        subjectId: String((r as Record<string, unknown>).id),
      })),
    )
      .then((m) => { if (alive) setCardTags(m); })
      .catch((e) => logErrorToJournal(e, 'tags.listSubjects'));
    return () => { alive = false; };
  }, [visibleRows, effectiveViewMode, type, objectName, tagsTick]);
  const hasSelection = selectedIds.size > 0;

  // ============================================================================
  // УНИВЕРСАЛЬНЫЕ ПАПКИ (metadata_folders): именованные отборы над списком.
  // kind: system/shared (видны всем) | user (личные). Спец-значения:
  // @current_user → id текущего пользователя.
  // ============================================================================
  interface FolderItem { code: string; name: string; synonym: string; filters: string; kind: string; ownerId: string }
  const [searchParams] = useSearchParams();
  const [folders, setFolders] = useState<FolderItem[]>([]);
  // Активная папка — '' = все; инициализируется из URL (?folder=) и
  // синхронизируется после загрузки папок (навигация из сайдбара/плиток)
  const [activeFolder, setActiveFolder] = useState<string>(searchParams.get('folder') || '');
  const [saveFolderOpen, setSaveFolderOpen] = useState(false);
  const [saveFolderName, setSaveFolderName] = useState('');
  const [saveFolderKind, setSaveFolderKind] = useState<'shared' | 'user'>('user');
  const myId = useAuthStore((s) => s.user?.id || '');
  const isAdminUser = useAuthStore((s2) => s2.isAdmin());

  useEffect(() => {
    if (!foldersPanel) return;
    let cancelled = false;
    void (async () => {
      try {
        const r: any = await getSDK().folders.listFolders({ entityType: type, entityCode: objectName });
        if (!cancelled) {
          const items = ((r?.items || []) as any[]).map((f) => ({
            code: f.code, name: f.name, synonym: f.synonym || f.name,
            filters: f.filters || '[]', kind: f.kind || 'shared', ownerId: f.ownerId || '',
          }));
          setFolders(items);
          // Папка из URL (навигация из сайдбара) — применяем, если существует
          const fromUrl = searchParams.get('folder');
          if (fromUrl) {
            setActiveFolder((cur) => (cur === fromUrl || items.some((x: FolderItem) => x.code === fromUrl) ? fromUrl : cur));
          }
        }
      } catch { /* папки недоступны — список работает без них */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersPanel, type, objectName]);

  // Резолв спец-значений в фильтрах активной папки (@current_user)
  const folderFilters = useMemo<ListFilter[] | undefined>(() => {
    if (!foldersPanel || !activeFolder) return undefined;
    const f = folders.find((x) => x.code === activeFolder);
    if (!f) return undefined;
    try {
      const parsed = JSON.parse(f.filters) as ListFilter[];
      return parsed.map((x) => ({ ...x, value: x.value === '@current_user' ? (myId || '') : (x.value ?? '') }));
    } catch { return undefined; }
  }, [foldersPanel, activeFolder, folders, myId]);

  // Итоговые видимые строки: папка → отборы списка → поиск
  const finalRows = useMemo(
    () => applySearch(applyFilters(folderFilters ? applyFilters(rows, folderFilters) : rows, effFilters), searchText),
    [rows, folderFilters, effFilters, searchText],
  );

  // Групповое проведение/отмена (как в 1С: выделили в списке → Провести):
  // для каждого выбранного документа postDocument/unpostDocument (модули
  // onPosting/onUnposting на сервере), затем перезагрузка списка.
  const handleBulkPost = async (unpost: boolean) => {
    if (selectedIds.size === 0) return;
    if (!await confirmDialog(t(unpost ? 'list.confirm_unpost' : 'list.confirm_post', { count: selectedIds.size }))) return;
    setBusy(true);
    try {
      const sdk = getSDK();
      for (const id of selectedIds) {
        if (unpost) {
          await sdk.metadata.unpostDocument({ documentType: table, documentId: id });
        } else {
          await sdk.metadata.postDocument({ documentType: table, documentId: id });
        }
      }
      setSelectedIds(new Set());
      await loadData();
    } catch (e: any) {
      setError(e?.message || t(unpost ? 'list.error.unpost' : 'list.error.post'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!await confirmDialog(t('list.confirm_mark_delete', { count: selectedIds.size }))) return;
    if (onDelete) {
      onDelete([...selectedIds]);
      setSelectedIds(new Set());
      await loadData();
      return;
    }
    for (const id of selectedIds) {
      await dataService.remove(schema, table, id);
    }
    setSelectedIds(new Set());
    await loadData();
  };

  // Физическое удаление (как в 1С: «Удалить окончательно») — только из списка
  // помеченных; право can_delete проверяет сервер
  const handleDeletePermanent = async () => {
    if (selectedIds.size === 0) return;
    if (!await confirmDialog(`Удалить окончательно ${selectedIds.size} записей? Действие необратимо.`)) return;
    try {
      const sdkPermanent = getSDK();
      for (const id of selectedIds) {
        await sdkPermanent.metadata.deleteTableRow({ schema, table, rowId: id, permanent: true });
      }
      setSelectedIds(new Set());
      await loadData();
    } catch (e: any) {
      log.error('Permanent delete failed', e);
      setError(e?.message || t('form.error.delete'));
    }
  };

  const handleRowDoubleClick = (row: Record<string, unknown>) => {
    onRowDoubleClick?.(row);
  };

  // Стандартная команда «Добавить группу» (как в 1С): новая группа создаётся
  // внутри последней развёрнутой группы (или в корне)
  const handleCreateGroup = async () => {
    if (!hierarchyType || type !== 'catalog') return;
    try {
      const parentId = [...expandedIds].pop() || '';
      await dataService.create(schema, table, { name: t('form.new_group'), is_group: true, parent_id: parentId });
      await loadData();
    } catch (e: any) {
      log.error('Create group failed', e);
      setError(e?.message || t('list.error.group'));
    }
  };

  // «Сохранить отбор как папку» (универсальные папки): текущий filters → metadata_folders.
  // kind: user — личная (владелец = я, сервер форсирует); shared — общая (админ).
  const handleSaveFolder = async () => {
    if (!saveFolderName.trim()) return;
    const code = 'f_' + Date.now().toString(36);
    try {
      await getSDK().folders.saveFolder({
        folder: {
          code,
          name: saveFolderName.trim(),
          synonym: saveFolderName.trim(),
          entityType: type,
          entityCode: objectName,
          filters: JSON.stringify(filters || []),
          kind: saveFolderKind,
          ownerId: useAuthStore.getState().user?.id || '',
          sortOrder: 100,
        },
      });
      setSaveFolderOpen(false);
      setSaveFolderName('');
      // обновить список папок
      const r: any = await getSDK().folders.listFolders({ entityType: type, entityCode: objectName });
      setFolders(((r?.items || []) as any[]).map((f) => ({
        code: f.code, name: f.name, synonym: f.synonym || f.name,
        filters: f.filters || '[]', kind: f.kind || 'shared', ownerId: f.ownerId || '',
      })));
      setActiveFolder(code);
    } catch (e: any) {
      setError(e?.message || t('list.error.folder'));
    }
  };

  return (
    <div className="relative flex flex-col flex-1 min-h-0">
      {/* ПАНЕЛЬ УНИВЕРСАЛЬНЫХ ПАПОК (именованные отборы; system/shared/user) */}
      {foldersPanel && folders.length > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b overflow-x-auto shrink-0">
          <button
            type="button"
            onClick={() => setActiveFolder('')}
            className={`text-[11px] whitespace-nowrap rounded-full px-2.5 py-0.5 border transition-colors ${activeFolder === '' ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
          >
            {t('list.all')}
          </button>
          {folders.map((f) => (
            <button
              key={f.code}
              type="button"
              title={`${t(f.kind === 'user' ? 'list.folder.user' : f.kind === 'system' ? 'list.folder.system' : 'list.folder.shared')}: ${f.filters}`}
              onClick={() => setActiveFolder(f.code)}
              className={`text-[11px] whitespace-nowrap rounded-full px-2.5 py-0.5 border transition-colors ${activeFolder === f.code ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
            >
              {f.synonym || f.name}
            </button>
          ))}
        </div>
      )}
      {/* Офлайн: «данные из кэша» (единый баннер; тот же ключ, что у чтения) */}
      {offlineBannerVisible && (
        <div className="px-2 pt-1.5 shrink-0">
          <OfflineDataBanner sourceKey={offlineBannerKey} />
        </div>
      )}
      {/* Панель стандартных команд списка */}
      {showToolbar && (
        <ListToolbar
          primary={[
            { key: 'create', label: t('list.create'), icon: <Plus className="h-3 w-3 mr-1" />, importance: 5, variant: 'default', visible: canCmd('create', rights?.canCreate) && !!onCreate && isElementVisible({ code: 'command:create' }), onClick: onCreate },
            { key: 'create_group', label: t('list.create_group'), icon: <Folder className="h-3 w-3 mr-1" />, importance: 4, visible: canCmd('create_group', rights?.canCreate) && !!hierarchyType, onClick: handleCreateGroup },
          ]}
          secondary={[
            { key: 'refresh', label: t('list.refresh'), icon: <RefreshCw className="h-3 w-3 mr-1" />, importance: 4, visible: canCmd('refresh'), onClick: loadData },
            { key: 'delete', label: t('list.mark_delete', { count: selectedIds.size }), icon: <Trash2 className="h-3 w-3 mr-1" />, importance: 3, variant: 'danger', visible: canCmd('delete', rights?.canDelete) && hasSelection, onClick: handleDelete },
            { key: 'delete_perm', label: t('list.delete_permanent', { count: selectedIds.size }), icon: <Trash2 className="h-3 w-3 mr-1" />, importance: 2, variant: 'danger', visible: showMarked && canCmd('delete', rights?.canDelete) && hasSelection, onClick: handleDeletePermanent },
            { key: 'post', label: t('list.post_count', { count: selectedIds.size }), icon: <Check className="h-3 w-3 mr-1" />, importance: 3, visible: isDocument && hasSelection && !showMarked, onClick: () => void handleBulkPost(false) },
            { key: 'unpost', label: t('list.unpost_count', { count: selectedIds.size }), icon: <X className="h-3 w-3 mr-1" />, importance: 3, visible: isDocument && hasSelection && !showMarked, onClick: () => void handleBulkPost(true) },
            { key: 'done_select', label: t('list.cancel'), icon: <X className="h-3 w-3 mr-1" />, importance: 5, visible: multiSelect, onClick: () => { setMultiSelect(false); setSelectedIds(new Set()); } },
            { key: 'save_folder', label: t('list.save_filter_folder'), icon: <Folder className="h-3 w-3 mr-1" />, importance: 2, visible: !!foldersPanel && !!(filters && filters.length), onClick: () => { setSaveFolderKind('user'); setSaveFolderName(''); setSaveFolderOpen(true); } },
            { key: 'show_marked', label: t('list.show_marked'), importance: 1, active: showMarked, visible: !!rights?.canViewMarked || objectType === 'task' || objectType === 'order', onClick: () => { setShowMarked((v) => !v); setSelectedIds(new Set()); } },
            { key: 'list_settings', label: t('list.settings'), icon: <Settings className="h-3 w-3 mr-1" />, importance: 1, visible: true, onClick: () => setListSettingsOpen(true) },
            { key: 'print_list', label: t('list.print'), icon: <Printer className="h-3 w-3 mr-1" />, importance: 2, visible: (objectType === 'catalog' || objectType === 'document' || objectType === 'order' || objectType === 'task') && finalRows.length > 0, onClick: () => setPrintListOpen(true) },
            { key: 'period', label: t('list.period'), icon: <CalendarDays className="h-3 w-3 mr-1" />, importance: 1, active: hasPeriod, visible: true, onClick: () => setPeriodOpen(true) },
          ]}
          searchValue={searchText}
          onSearchChange={setSearchText}
          showSearch={hasCmd('search')}
          voiceSearch={voiceSearch}
          trailing={filters && filters.length > 0 ? (
            <span className="text-[10px] text-muted-foreground shrink-0">{t('list.active_filter', { count: filters.length })}</span>
          ) : undefined}
        />
      )}
      {/* Спиннер/ошибка — ТОЛЬКО в области данных (тулбар/поиск остаются:
          «Обновить» не перерисовывает панель команд). */}
      {loading && (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}
      {!loading && error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2 m-2">
          <span className="flex-1 min-w-0 break-words">{error}</span>
          <CopyButton text={error} compact />
        </div>
      )}
      {!loading && !error && (() => {
        const renderItems = (items: Record<string, unknown>[]) => (effectiveViewMode === 'cards' ? (
    <CardGrid>
      {(narrow) => (
        buildTreeRows(items).length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-12">Нет данных</div>
        ) : (
          buildTreeRows(items).map((row) => {
            const cardFields: CardField[] = columns
              .filter((c) => c.key !== '__tree' && c.key !== 'id')
              .map((c) => ({
                name: c.key,
                synonym: c.label,
                dataType: (c as any).dataType || 'text',
                importance: Number((c as any).importance) || 3,
                format: (c as any).format || '',
              }));
            const presentations: Record<string, string> = {};
            for (const c of columns) {
              if ((c as any).isRef && row[`${c.key}_presentation`] !== undefined) {
                presentations[c.key + '_presentation'] = String(row[`${c.key}_presentation`]);
              }
            }
            const rowId = String(row.id);
            const isSelected = selectedIds.has(rowId);
            return (
              <ObjectCard
                key={rowId}
                fields={cardFields}
                data={row as Record<string, unknown>}
                qualityMark={(() => { try { return String(JSON.parse(String((row as any).quality || '{}')).overall || ''); } catch { return ''; } })()}
                presentations={presentations}
                enumSynonyms={enumLabels}
                onClick={() => {
                  // Клик ПОСЛЕ удержания подавляем — выделение уже переключено (клик = открытие)
                  if (longPressFired.current) { longPressFired.current = false; return; }
                  // КЛИК ВСЕГДА открывает объект (удержание — выделение, во всех видах).
                  if (singleClickOpen) { onRowDoubleClick?.(row); return; }
                  if (onSelect) onSelect(row);
                  else onRowDoubleClick?.(row);
                }}
                onMouseDown={() => startLongPress(rowId)}
                onTouchStart={() => startLongPress(rowId)}
                onMouseUp={cancelLongPress}
                onTouchEnd={cancelLongPress}
                onMouseLeave={cancelLongPress}
                variant="card"
                template={cardTemplateDef}
                unreadCount={chatUnreadFor(type, rowId)}
                tags={cardTags[rowId]}
                className={`${cardNarrowClass(narrow)} ${isSelected ? 'ring-2 ring-primary border-primary' : ''}`}
              />
            );
          })
        )
      )}
    </CardGrid>
  ) : (
        <div className="flex-1 min-h-0">
          <DataTable
            columns={[
              // Дерево иерархического справочника (как в 1С): отступы + разворот групп
              ...(showTree ? [{
                key: '__tree',
                label: '',
                sortable: false,
                width: 40,
                render: (_v: unknown, row?: Record<string, unknown>) => (
                  <span style={{ paddingLeft: (Number((row as any)?.__depth || 0)) * 14 }} className="flex items-center gap-1">
                    {(row as any)?.__hasChildren ? (
                      <button
                        className="text-[10px] w-4 text-center"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(String((row as any).id)); }}
                        title={(row as any)?.__expanded ? t('list.collapse') : t('list.expand')}
                      >
                        {(row as any)?.__expanded ? '▼' : '▶'}
                      </button>
                    ) : <span className="w-4" />}
                    {(row as any)?.__isGroup ? <Folder className="h-3.5 w-3.5 text-amber-500" /> : null}
                  </span>
                ),
              } as any] : []),
              // Бейдж «обсуждение» 💬N у имени (первая колонка) — табличные виды
              ...decorateWithChatBadge(columns as any, type, chatUnreadFor),
            ]}
            data={buildTreeRows(items)}
            rowKey="id"
            showFooter={false}
            compact
            selectable
            noCheckboxes
            rowStyle={(row) => {
              // Пометка на удаление (как в 1С): строка приглушается цветом
              // (opacity перекрывается анимацией framer-motion) — ✕ в колонке «Пометка удаления»
              const marked = row.is_marked === true || row.is_marked === 'true' || row.is_marked === '1';
              const base: React.CSSProperties = marked ? { color: 'hsl(var(--muted-foreground))' } : {};
              const fmt = applyFormats(condRules, row, condCtx, 'row');
              const attrs = fmt.styleId ? condStyles[fmt.styleId] : undefined;
              const css = attrs ? styleCss(attrs) : {};
              return { ...css, ...base };
            }}
            onSelectionChange={(keys) => setSelectedIds(new Set([...keys].map(String)))}
            selectedKeys={selectedIds}
            selectionActive={multiSelect}
            onLongPress={() => setMultiSelect(true)}
            hideToolbar
            hideSearch={showToolbar || !hasCmd('search')}
            onRowDoubleClick={handleRowDoubleClick}
            onRowClick={singleClickOpen ? handleRowDoubleClick : onSelect}
          />
        </div>
    ));
    // ГРУППИРОВКИ: без настроек — обычный список; иначе единый слой группировок
    // (секции/канбан/чипы/карточки-группы с провалом) для ЛЮБЫХ форм списков.
    // effGroupMode/effGroupBy — с учётом пользовательских НАСТРОЕК («Ещё → Настройки»).
    if (groupFields.length === 0 || !effGroupMode) return renderItems(finalRows as Record<string, unknown>[]);
    return (
      <GroupedList
        mode={String(effGroupMode)}
        fields={groupFields}
        rows={finalRows as Record<string, unknown>[]}
        valueOf={groupValueOf}
        labelFor={groupLabel}
        persistKey={`${objectType}:${objectName}`}
        renderItems={renderItems}
      />
    );
  })()}

      {/* «Показать ещё» — следующая страница курсорной выдачи (задачи/заявки):
          keyset без OFFSET, nextCursor приходит с сервера (стабильно на больших базах). */}
      {!loading && !error && (nextCursor !== '' || loadingMore) && (
        <div className="flex justify-center py-2 shrink-0">
          <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => void loadMore()}>
            {loadingMore ? t('common.loading') : t('list.show_more')}
          </Button>
        </div>
      )}

      {/* Диалог «Сохранить отбор как папку» */}
      {saveFolderOpen && (
        <UniversalWindow open onClose={() => setSaveFolderOpen(false)} title={t('list.save_folder_title')} width="w-[min(440px,92vw)]" resizable={false}>
          <div className="p-3 space-y-3">
            <div>
              <Label className="text-xs">{t('list.save_folder_name')}</Label>
              <Input value={saveFolderName} onChange={(e) => setSaveFolderName(e.target.value)} placeholder={t('list.save_folder_placeholder')} autoFocus />
            </div>
            {isAdminUser && (
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={saveFolderKind === 'shared'} onChange={(e) => setSaveFolderKind(e.target.checked ? 'shared' : 'user')} className="h-4 w-4" />
                {t('list.shared_folder')}
              </label>
            )}
            <p className="text-[10px] text-muted-foreground">
              {t('list.save_folder_hint', { count: filters?.length || 0 })}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setSaveFolderOpen(false)}>{t('common.cancel')}</Button>
              <Button size="sm" onClick={() => void handleSaveFolder()} disabled={!saveFolderName.trim()}>{t('common.save')}</Button>
            </div>
          </div>
        </UniversalWindow>
      )}

      {/* НАСТРОЙКИ СПИСКА (SRP/SSoT, per-user): «Ещё → Настройки» → вкладка
          «Группировки» (вкл/выкл, вариант, поля, вложенность). Перекрывают
          настройки формы/виджета; хранятся в user_settings (settingsStore). */}
      <ListSettingsDialog
        open={listSettingsOpen}
        objectType={objectType}
        objectName={objectName}
        mode={String(effGroupMode)}
        by={String(effGroupBy)}
        filters={userFilters}
        hasOverride={!!groupOverride || userFilters.length > 0}
        fields={columns.map((c) => ({ key: String(c.key), label: String(c.label), dataType: String((c as Record<string, unknown>).dataType || '') }))}
        onApply={(mode, by, flt) => {
          settingsStore.setJSON(groupSettingsKey, { mode, by });
          settingsStore.setJSON(filtersSettingsKey, flt);
        }}
        onReset={() => { settingsStore.set(groupSettingsKey, ''); settingsStore.set(filtersSettingsKey, ''); }}
        onClose={() => setListSettingsOpen(false)}
      />

      {/* ПЕРИОД (по дате создания) — единая форма (SSoT/SRP): быстрые пресеты +
          произвольные даты + сброс; пишет пользовательские отборы created_at
          «с/по» (тот же settingsStore/applyFilters — без отдельных путей). */}
      {periodOpen && (
        <PeriodFilterDialog
          open
          from={periodFrom}
          to={periodTo}
          onApply={applyPeriod}
          onReset={resetPeriod}
          onClose={() => setPeriodOpen(false)}
        />
      )}

      {/* ПЕЧАТЬ СПИСКА — те же печатные формы объекта, рендер ПО СТРОКАМ
          (params.rows): сервер повторяет «область данных» макета для каждой
          строки. SSoT механики — сервер. */}
      {printListOpen && (
        <PrintListDialog
          objectType={objectType}
          objectName={objectName}
          rows={finalRows as Record<string, unknown>[]}
          onClose={() => setPrintListOpen(false)}
        />
      )}
    </div>
  );
}
